import { Rule, RuleSetter } from "../rules";
import {
  ComparisonOptions,
  DateOptions,
  DimensionOptions,
  Filter as Filter2,
  KpiOptions,
  PickupOptions,
  StatusOptions,
} from "../../services/Alert";
import { Operator } from "../../constants";

export enum FilterType {
  DATE = "date",
  KPI = "kpi",
  MULTIPLE = "multiple",
  STATUS = "status",
  PICKUP = "pickup",
  COMPARISON = "comparison",
  DATE_RANGE = "date_range",
}

export interface Filter {
  // id: number;
  // TODO: remove the name ?
  name?: string;
  label: string;
  parent: string;
  options?: object;
  type: FilterType;
}

export interface FilterDimension extends Filter2 {
  target: number;
  type: FilterType.MULTIPLE;
  options: DimensionOptions;
}

export interface FilterDate extends Filter2 {
  target: null;
  type: FilterType.DATE;
  options: DateOptions;
}

export interface FilterDateRange extends Filter2 {
  target: number;
  type: FilterType.DATE_RANGE;
  options: {
    values: string[];
  };
}

export interface FilterStatus extends Filter2 {
  target: 10000;
  type: FilterType.STATUS;
  options: StatusOptions;
}

export interface FilterPickup extends Filter2 {
  target: null;
  type: FilterType.PICKUP;
  options: PickupOptions;
}

export interface FilterComparison extends Filter2 {
  target: null;
  type: FilterType.COMPARISON;
  options: ComparisonOptions;
}

export enum FormatType {
  BOOLEAN = "boolean",
  /** ✅ / ❌ instead of Oui / Non, for a narrow status column. */
  BOOLEAN_EMOJI = "boolean-emoji",
  CALCUL_EDITOR = "calcul_editor",
  COLOR = "color",
  CURRENCY = "currency",
  DATE = "date",
  /** Translated as `translationBaseKey` + the value uppercased: `trade_show` → `…TRADE_SHOW`. */
  ENUM = "enum",
  EVENT = "event",
  /** A value's position on a fixed scale, as a cursor on a rail. */
  GAUGE = "gauge",
  ICON = "icon",
  /** `EventImpact`, translated and coloured by weight. */
  IMPACT = "impact",
  MODEL_COLOR = "model-color",
  NUMBER = "number",
  PERCENT = "percent",
  PROGRESS = "progress",
  PROGRESS_CURRENCY = "progressCurrency",
  TEXT = "text",
  /**
   * Sometimes, it's necessary when we have a "null" value to display a text instead of nothing.
   * Ex: If autopilot-rule.properties.hebergement_nom_court is null, we want to display "All".
   */
  TEXT_FALLBACK_ALL = "text-fallback-all",
  TEXT_FALLBACK_ALL_COUNT = "text-fallback-all-count",
  TRANSLATION = "translation",
  WEEKDAYS = "weekdays",
  WEEKDAYS_ROUNDED = "weekdays-rounded",
  BUSINESS_RULE_PERIMETER = "business-rule-perimeter",
  BUSINESS_RULE_CONDITIONS = "business-rule-conditions",
  BUSINESS_RULE_ACTION = "business-rule-action",
  LIST = "list",
}

export interface FormatRule {
  /**
   * The type of rule to check
   */
  type: Rule;
  /**
   * the value to compare
   * TODO: move that in an options nested variable
   */
  value: number;
  /**
   * The operator for the comparison
   * TODO: move that in an options nested variable
   */
  operator: Operator;
  /**
   * The type of action to do if the rule is true
   */
  format: RuleSetter;
  /**
   * The
   */
  options: { [key: string]: string };
}

export interface BaseOptions {
  type: Exclude<FormatType, FormatType.WEEKDAYS>;
  kUnit?: boolean;
  maximumFractionDigits?: number;
  multiply?: boolean;
  round?: boolean;
  style?: { [key: string]: string };
  rules?: Array<FormatRule>;
  use_property_currency?: boolean;
}

export interface CurrencyOptions extends BaseOptions {
  currencyCode: string;
}

export interface GaugeBand {
  /** Upper bound of the band, in the KPI's own units. The last one carries `max`. */
  to: number;
  /** Theme token, e.g. "success.main". */
  color: string;
}

export interface GaugeOptions extends BaseOptions {
  /** Scale bounds, in the KPI's own units. */
  min: number;
  max: number;
  /**
   * Where the bar fills from. Omit for a plain 0..1 proportion, which fills from the
   * left; set 0 on a signed KPI, which fills left or right of its own zero so the
   * direction *is* the reading.
   */
  center?: number;
  /**
   * Colour of the fill, by the band the value lands in. Thresholds are a business
   * call, so they live in the KPI's format rather than in the component.
   *
   * ⚠️ One band means "this quantity has no good or bad". Only declare several where
   * the scale really carries a judgement — a signed gap does, a demand level does
   * not, and painting a level red asserts a verdict nobody made.
   */
  bands?: GaugeBand[];
}

export enum IconDefinition {
  ARROW_RIGHT = "arrow-right",
  ARROW_UP = "arrow-up",
  ARROW_DOWN = "arrow-down",
  ALERTE_PU_ARROW_DOWN = "alerte-pu-arrow-down",
  ALERTE_PU_ARROW_RIGHT = "alerte-pu-arrow-right",
  ALERTE_PU_ARROW_UP = "alerte-pu-arrow-up",
  ALERTE_PU_INDIV_ARROW_DOWN = "alerte-pu-indiv-arrow-down",
  ALERTE_PU_INDIV_ARROW_RIGHT = "alerte-pu-indiv-arrow-right",
  ALERTE_PU_INDIV_ARROW_UP = "alerte-pu-indiv-arrow-up",
  BED = "bed",
  CHECK = "check",
  CHEVRON_DOWN = "chevron-down",
  CHEVRON_LEFT = "chevron-left",
  CHEVRON_LEFT_DISABLE = "chevron-left-disable",
  CHEVRON_RIGHT = "chevron-right",
  CHEVRON_RIGHT_DISABLE = "chevron-right-disable",
  CHEVRON_UP = "chevron-up",
  CLOSED = "closed",
  GLASS_LOW_LEVEL = "glass-low-level",
  GLASS_MEDIUM_LEVEL = "glass-medium-level",
  GLASS_HIGH_LEVEL = "glass-high-level",
  GRIP_LINES = "grip-lines",
  HIGHLIGHT_OFF = "highlight-off",
  JAUGE_DOWN = "jauge-down",
  JAUGE_MEDIUM = "jauge-medium",
  JAUGE_UP = "jauge-up",
  NOTIFICATIONS = "notifications",
  OPEN = "open",
  RECO = "reco",
  RECO_MIN_SET = "reco-min-set",
  RECO_MIN_SET_DISABLE = "reco-min-set-disable",
  RECO_MIN_REMOVE = "reco-min-remove",
  RECO_MIN_REMOVE_DISABLE = "reco-min-remove-disable",
  RECO_PRICE_DOWN = "reco-price-down",
  RECO_PRICE_DOWN_DISABLE = "reco-price-down-disable",
  RECO_PRICE_UP = "reco-price-up",
  RECO_PRICE_UP_DISABLE = "reco-price-up-disable",
  SNOOZE = "snooze",
  STAR = "star",
  SYNC_ALT = "sync-alt",
  TRENDING_DOWN = "trending-down",
  TRENDING_UP = "trending-up",
  VERTICAL_ALIGN_TOP = "vertical-align-top",
}

export interface IconOptions extends BaseOptions {
  icon: IconDefinition | { value: number; icon: IconDefinition }[];
  choices?: { label: string; value: number }[];
}

export type KpiFormat =
  | BaseOptions
  | CurrencyOptions
  | GaugeOptions
  | IconOptions;

export interface KpiBase {
  id: number;
  label: string;
  order: number;
}

export interface Kpi {
  id: number;
  /** The KPI code (`kpi_code`), the key of `kpis` in an alert list row. */
  name: string;
  label: string;
  order: number;
  format: KpiFormat;
}

export interface KpiWithColumnWidth extends Kpi {
  columnWidth?: number;
}

export interface KpiWithGroupId extends Kpi {
  groupId: number;
}

export interface KpiType {
  id: number;
  label: string;
  order: number;
  children?: Kpi[];
}

export interface FilterKpi extends Filter2 {
  target: number;
  type: FilterType.KPI;
  options: KpiOptions;
}

export interface FilterKpiOptions {
  // id of the category
  parent: number;
  operator: Operator;
  // target or kpi ?
  // target: number;
  kpi: number;
  value: number;
}
export interface OptionsFormatCurrency extends Intl.NumberFormatOptions {
  roundCurrency?: boolean;
  kUnit?: boolean;
  maximumFractionDigits?: number;
}
